// A constellation's lines form a tree (each star after the first joins one
// earlier star, select.ts), until a ringed planet adds one closing line as
// the last entry of `lines` (planet.ts). These helpers read a constellation
// either way.
import type { Constellation, Line } from './types';

/** The nodes from `from` to `to` along the tree's lines, both ends included; null when not joined. */
export function treePath(lines: readonly Line[], from: number, to: number): number[] | null {
  if (from === to) return [from];
  const parent = new Map<number, number>([[from, from]]);
  const queue = [from];
  while (queue.length > 0) {
    const at = queue.shift()!;
    for (const [a, b] of lines) {
      const next = a === at ? b : b === at ? a : null;
      if (next === null || parent.has(next)) continue;
      parent.set(next, at);
      if (next === to) {
        const path = [to];
        for (let n = to; n !== from; ) {
          n = parent.get(n)!;
          path.push(n);
        }
        return path.reverse();
      }
      queue.push(next);
    }
  }
  return null;
}

/** The lines the player drew: every line but a ringed planet's closing one. */
export function treeLines(c: Constellation): Line[] {
  return c.planet ? c.lines.slice(0, -1) : c.lines;
}

/** The line the game added to ring the planet, or null. */
export function closingLine(c: Constellation): Line | null {
  return c.planet ? (c.lines[c.lines.length - 1] ?? null) : null;
}
